import React, { Fragment } from "react"
import Slider from "react-slick"
import styled from "styled-components"
import breakpoints from "../../styles/breakpoints"
import colors from "../../styles/colors"
import fonts from "../../styles/fonts"
import { useLocation } from "@reach/router"
import { withTranslation } from "gatsby-plugin-react-i18next"
import { Link } from "gatsby"
import WeddingFilter from "./WeddingFilter"
import { getIsFrance } from "../../services/locale"
import {
  getBespokeLink,
  getEngagementPlpLink,
  getWeddingLink,
} from "../../services/links"

const NavigationContainer = styled.div`
  max-width: 100vw;
  overflow: hidden;

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`

const StyledSlider = styled(Slider)`
  background: ${colors.secondaryBeigeLight};
  padding: 10px 0;
  width: 100vw;

  .slick-list {
    overflow: visible;
  }

  .slick-track {
    display: flex;
    min-width: 100%;
    overflow-x: auto;
  }

  .slick-slide {
    display: flex !important;
    height: 18px;
    align-items: center;

    div {
      display: flex;
      align-items: center;
      height: inherit;
    }
  }
`

const StyledLink = styled(Link)`
  ${fonts.openSans};
  text-transform: lowercase;
  padding: 0 16px;
  font-size: 10px;
  line-height: 18px;
  color: ${colors.trueBlack};
  font-weight: 300;
  text-decoration: ${(props) => (props.active ? "underline" : "none")};
  display: inline-block;
  position: relative;
  text-wrap-mode: nowrap;
`

const WeddingFilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px auto;
  padding: 0 20px;
  width: 100%;
`

const MobileNavigation = ({ wedding, engagement, t }) => {
  const activeEnv = process.env.GATSBY_ACTIVE_ENV || "us"
  const location = useLocation()
  const isFrance = getIsFrance()

  const navigation = [
    {
      title: t("plp.navigation.shop_all"),
      link: "/shop-jewelry/",
      relatedLinks: [
        "/shop-jewelry/",
        isFrance ? "/shop-or-jaune-jewelry/" : "/shop-yellow-gold-jewelry/",
        isFrance ? "/shop-or-blanc-jewelry/" : "/shop-white-gold-jewelry/",
        isFrance ? "/shop-or-rose-jewelry/" : "/shop-rose-gold-jewelry/",
      ],
      categories: [
        { title: t("plp.navigation.shop_all"), link: "/shop-jewelry/" },
        {
          title: t("plp.navigation.earrings"),
          link: t("link.earrings"),
        },
        {
          title: t("plp.navigation.rings"),
          link: t("link.rings"),
        },
        {
          title: t("plp.navigation.necklaces"),
          link: t("link.necklaces"),
        },
        { title: t("plp.navigation.bracelets"), link: t("link.bracelets") },
      ],
    },
    {
      title: t("plp.navigation.earrings"),
      link: t("link.earrings"),
      relatedLinks: [
        t("link.earrings"),
        isFrance ? "/or-jaune-boucles-d-oreilles/" : "/yellow-gold-earrings/",
        isFrance ? "/or-blanc-boucles-d-oreilles/" : "/white-gold-earrings/",
        isFrance ? "/or-rose-boucles-d-oreilles/" : "/rose-gold-earrings/",
        isFrance ? "/or-blanc-creoles/" : "/white-gold-hoops/",
        isFrance ? "/or-jaune-creoles/" : "/yellow-gold-hoops/",
        isFrance ? "/or-rose-creoles/" : "/rose-gold-hoops/",
        isFrance ? "/or-jaune-piercings/" : "/yellow-gold-piercings/",
        isFrance ? "/or-blanc-piercings/" : "/white-gold-piercings/",
        isFrance ? "/or-rose-piercings/" : "/rose-gold-piercings/",
        isFrance ? "/or-jaune-paires/" : "/yellow-gold-pairs/",
        isFrance ? "/or-blanc-paires/" : "/white-gold-pairs/",
        isFrance ? "/or-rose-paires/" : "/rose-gold-pairs/",
        "/yellow-gold-ear-climbers/",
        "/white-gold-ear-climbers/",
        "/rose-gold-ear-climbers/",
        isFrance ? "/or-jaune-ear-cuffs/" : "/yellow-gold-ear-cuffs/",
        isFrance ? "/or-blanc-ear-cuffs/" : "/white-gold-ear-cuffs/",
        isFrance ? "/or-rose-ear-cuffs/" : "/rose-gold-ear-cuffs/",
        "/yellow-gold-statement-earrings/",
        "/white-gold-statement-earrings/",
        "/rose-gold-statement-earrings/",
        t("link.statement_earrings"),
        t("link.ear_cuffs"),
        t("link.ear_climbers"),
        t("link.pairs"),
        t("link.piercings"),
        t("link.hoops"),
      ],
      categories: [
        {
          title: t("plp.mobile.navigation.earrings.all"),
          link: t("link.earrings"),
        },
        {
          title: t("plp.navigation.hoops"),
          link: t("link.hoops"),
        },
        {
          title: t("plp.navigation.piercings"),
          link: t("link.piercings"),
        },
        {
          title: t("plp.navigation.pairs"),
          link: t("link.pairs"),
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.ear_climbers"),
              link: t("link.ear_climbers"),
            },
        {
          title: t("plp.navigation.ear_cuffs"),
          link: t("link.ear_cuffs"),
        },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.statement"),
              link: t("link.statement_earrings"),
            },
      ],
    },
    {
      title: t("plp.navigation.rings"),
      link: t("link.rings"),
      relatedLinks: [
        t("link.rings"),
        isFrance ? "/or-jaune-bagues/" : "/yellow-gold-rings/",
        isFrance ? "/or-blanc-bagues/" : "/white-gold-rings/",
        isFrance ? "/or-rose-bagues/" : "/rose-gold-rings/",
        t("link.statement_rings"),
        "/yellow-gold-statement-rings/",
        "/white-gold-statement-rings/",
        "/rose-gold-statement-rings/",
        t("link.stackable_rings"),
        "/yellow-gold-stackable-rings/",
        "/white-gold-stackable-rings/",
        "/rose-gold-stackable-rings/",
        t("link.pinky_rings"),
        "/yellow-gold-pinky-rings/",
        "/white-gold-pinky-rings/",
        "/rose-gold-pinky-rings/",
        t("link.eternity_rings"),
        "/yellow-gold-eternity-rings/",
        "/white-gold-eternity-rings/",
        "/rose-gold-eternity-rings/",
      ],
      categories: [
        {
          title: t("plp.mobile.navigation.rings.all"),
          link: t("link.rings"),
        },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.statement"),
              link: t("link.statement_rings"),
            },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.stackable"),
              link: t("link.stackable_rings"),
            },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.pinky"),
              link: t("link.pinky_rings"),
            },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.eternity"),
              link: t("link.eternity_rings"),
            },
      ],
    },
    {
      title: t("plp.navigation.necklaces"),
      link: t("link.necklaces"),
      relatedLinks: [
        t("link.necklaces"),
        isFrance ? "/or-jaune-colliers/" : "/yellow-gold-necklaces/",
        isFrance ? "/or-blanc-colliers/" : "/white-gold-necklaces/",
        isFrance ? "/or-rose-colliers/" : "/rose-gold-necklaces/",

        t("link.personalized_necklaces"),
        isFrance
          ? "/or-jaune-colliers-personnalises/"
          : "/yellow-gold-personalized-necklaces/",
        isFrance
          ? "/or-blanc-colliers-personnalises/"
          : "/white-gold-personalized-necklaces/",
        isFrance
          ? "/or-rose-colliers-personnalises/"
          : "/rose-gold-personalized-necklaces/",
        t("link.statement_necklaces"),
        "/yellow-gold-statement-necklaces/",
        "/white-gold-statement-necklaces/",
        "/rose-gold-statement-necklaces/",
        t("link.solitaire_necklaces"),
        isFrance
          ? "/or-jaune-colliers-solitaire/"
          : "/yellow-gold-solitaire-necklaces/",
        isFrance
          ? "/or-blanc-colliers-solitaire/"
          : "/white-gold-solitaire-necklaces/",
        isFrance
          ? "/or-rose-colliers-solitaire/"
          : "/rose-gold-solitaire-necklaces/",
        t("link.layerable_necklaces"),
        "/yellow-gold-layerable-necklaces/",
        "/white-gold-layerable-necklaces/",
        "/rose-gold-layerable-necklaces/",
      ],
      categories: [
        {
          title: t("plp.mobile.navigation.necklaces.all"),
          link: t("link.necklaces"),
        },
        {
          title: t("plp.mobile.navigation.personalized"),
          link: t("link.personalized_necklaces"),
        },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.statement"),
              link: t("link.statement_necklaces"),
            },
        {
          title: t("plp.mobile.navigation.solitaire"),
          link: t("link.solitaire_necklaces"),
        },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.layerable"),
              link: t("link.layerable_necklaces"),
            },
      ],
    },
    {
      title: t("plp.navigation.bracelets"),
      link: t("link.bracelets"),
      relatedLinks: [
        t("link.bracelets"),
        isFrance ? "/or-jaune-bracelets/" : "/yellow-gold-bracelets/",
        isFrance ? "/or-blanc-bracelets/" : "/white-gold-bracelets/",
        isFrance ? "/or-rose-bracelets/" : "/rose-gold-bracelets/",
        t("link.personalized_bracelets"),
        isFrance
          ? "/or-jaune-bracelets-personnalises"
          : "/yellow-gold-personalized-bracelets/",
        isFrance
          ? "/or-blanc-bracelets-personnalises"
          : "/white-gold-personalized-bracelets/",
        isFrance
          ? "/or-rose-bracelets-personnalises"
          : "/rose-gold-personalized-bracelets/",
        t("link.bangle_bracelets"),
        "/yellow-gold-bangle-bracelets/",
        "/white-gold-bangle-bracelets/",
        "/rose-gold-bangle-bracelets/",
        t("link.cord_bracelets"),
        isFrance
          ? "/or-jaune-bracelets-corde/"
          : "/yellow-gold-cord-bracelets/",
        isFrance ? "/or-blanc-bracelets-corde/" : "/white-gold-cord-bracelets/",
        isFrance ? "/or-rose-bracelets-corde/" : "/rose-gold-cord-bracelets/",
        t("link.bracelets_for_him"),
        isFrance
          ? "/or-jaune-bracelets-pour-lui/"
          : "/yellow-gold-bracelets-for-him/",
        isFrance
          ? "/or-blanc-bracelets-pour-lui/"
          : "/white-gold-bracelets-for-him/",
        isFrance
          ? "/or-rose-bracelets-pour-lui/"
          : "/rose-gold-bracelets-for-him/",
      ],
      categories: [
        {
          title: t("plp.mobile.navigation.bracelets.all"),
          link: t("link.bracelets"),
        },
        {
          title: t("plp.mobile.navigation.personalized"),
          link: t("link.personalized_bracelets"),
        },
        isFrance
          ? null
          : {
              title: t("plp.mobile.navigation.bangle"),
              link: t("link.bangle_bracelets"),
            },
        {
          title: t("plp.mobile.navigation.cord"),
          link: t("link.cord_bracelets"),
        },
        {
          title: t("plp.mobile.navigation.for him"),
          link: t("link.bracelets_for_him"),
        },
      ],
    },
    {
      title: t("plp.mobile.navigation.rings.all"),
      link: getEngagementPlpLink(),
      relatedLinks: [
        getEngagementPlpLink(),
        getEngagementPlpLink("yellow gold"),
        getEngagementPlpLink("white gold"),
        getEngagementPlpLink("rose gold"),
      ],
      categories: [
        {
          title: t("plp.mobile.navigation.rings.all"),
          link: getEngagementPlpLink(),
        },
        isFrance
          ? null
          : {
              title: t("plp.navigation.wedding_bands"),
              link: getWeddingLink(),
            },
        {
          title: t("plp.mobile.navigation.bridal_jewelry"),
          link: "/wedding-day/",
        },
        {
          title: t("plp.navigation.bespoke_service"),
          link: getBespokeLink(),
        },
      ],
    },
  ]

  const pathname =
    activeEnv === "us"
      ? location.pathname
      : location.pathname.replace(`/${activeEnv}/`, "/")

  let activeNav = navigation.find((nav) => nav.relatedLinks?.includes(pathname))

  if (!activeNav) {
    activeNav = navigation[0]
  }

  const getActiveStatus = (link) => {
    return pathname === link
  }
  const activeLinkIndex = activeNav?.categories?.findIndex(
    (link) => link && getActiveStatus(link?.link)
  )

  const settings = {
    variableWidth: true,
    arrows: false,
    initialSlide: activeLinkIndex === -1 ? 0 : activeLinkIndex,
    swipeToSlide: true,
  }

  return (
    <NavigationContainer>
      {activeNav && activeNav.categories && !wedding && (
        <StyledSlider {...settings}>
          {activeNav.categories.map((link, index) => {
            if (!link) return null
            const isActive = getActiveStatus(link.link)
            return (
              <Fragment key={index}>
                <StyledLink active={isActive ? 1 : 0} to={link.link}>
                  {link.title}
                </StyledLink>
                <span>|</span>
              </Fragment>
            )
          })}
        </StyledSlider>
      )}
      {wedding && (
        <WeddingFilterContainer>
          <WeddingFilter />
        </WeddingFilterContainer>
      )}
    </NavigationContainer>
  )
}

export default withTranslation()(MobileNavigation)
